import { MyTimesheetStatusComponent } from 'components/Reports/MyTimesheetStatusComponent';
import { Box, CardHeader, Typography, Grid, Card, CardContent, LinearProgress } from '@mui/material';
import { useState, useEffect, FC } from 'react';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import GenericCalendar from '../TimeOff/Calendar/GenericCalendar';
import { ENTITY_TYPE } from 'common/constants';
import { getUserEntity } from 'common/UserEntityProvider';
import { useTransition } from 'react';
import { DashboardContent } from 'layouts/dashboard';
import { useNotificationQueryClient } from 'store/registry/useNotificationsStorage';
import { useCalendarEvents } from 'store/registry/useCalendarEvents';
import { startOfMonth, endOfMonth } from 'date-fns';

export type EventType = {
    eventType: string,
    id: number,
    title: string,
    from: string,
    to: string | null,
    idEmployee: number | null,
    employeeName: string | null,
    employeeLastName: string | null,
    idWorkflowState: number | null,
    workflowStateName: string | null,
    workflowStateColor: string | null,
    changeLog: string | null,
    idTimeOffRequestType: number | null,
    timeOffRequestTypeName: string | null,
    idCountry: number | null,
    country: {
        name: string,
        "code": string,
        "phonePrefix": string,
        "id": number,
        "isDeleted": boolean
    }
};

export type SearchResult = {
	totalRecords: number | null,
	queryResult: EventType[] | any
};

export const DashboardComponent: FC = () => {
    const [errors, setErrors] = useState({});
    const toast = useToastMessageQueue();
    
    // Use date range for current month by default
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
        const now = new Date();
        return {
            start: startOfMonth(now),
            end: endOfMonth(now)
        };
    });

    const userEntity = getUserEntity();
    const isEmployee = userEntity?.entityType === ENTITY_TYPE.EMPLOYEE;

    // Use the calendar events hook with caching — only for employees
    const { data: events, isLoading: isLoadingCalendar, refetch } = useCalendarEvents(
        dateRange.start,
        dateRange.end,
        undefined,
        isEmployee
    );

    // Transform events for display
    const transformedEvents = events.map(event => {
        // Ensure dates are Date objects
        const startDate = event.start instanceof Date ? event.start : new Date(event.start);
        return {
            ...event,
            start: startDate,
            end: startDate // Use start date as end date for dashboard display
        };
    });

    const handleMonthChange = (start: Date, end: Date) => {
        setDateRange({ start, end });
    };

    const { refetch: refetchNotifications } = useNotificationQueryClient();

    return (
        
        <DashboardContent maxWidth="xl">
            <Typography variant="h4" color='primary' sx={{ my: 3 }}>
                {`${intl.get('dashboard.welcomeback')}, ${userEntity?.entityName}!`}
            </Typography>

            <Grid container spacing={3}>
                {isEmployee && (
                    <>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ height: '100%', minHeight: 350 }}>
                                <MyTimesheetStatusComponent
                                    key="timesheet"
                                    errors={errors}
                                    setErrors={setErrors}
                                />
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardHeader
                                    title={
                                        <Typography color="primary" variant="h6" component="h6">
                                            {intl.get('dashboardComponent.calendar.title')}
                                        </Typography>
                                    }
                                />
                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 0 }}>
                                    <GenericCalendar
                                        events={transformedEvents}
                                        isCalendarDashboard={true}
                                        updateEvents={refetch}
                                        onMonthChange={handleMonthChange}
                                        isLoading={isLoadingCalendar}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
            </Grid>
        </DashboardContent>
        
    )
};
